import React, { useState, useCallback, useMemo } from "react";
import { useDispatch } from "react-redux";
import {
  SignupFlowFooter,
  SignupFlowContent,
  SignupFlowWrapperContainer,
  FooterRight,
} from "../Components/SignupFlowWrapper.styles";
import { ChoiceWrapper } from "../Components/SignupContainer.styles";
import { Button } from "components/Button";
import { useQuestionnaire } from "shared/hooks/useQuestionnaire";
import { Loader } from "components/Loader";
import RadioGroup from "components/RadioGroup/index";
import CheckboxGroup from "components/CheckboxGroup/index";
import { saveQuestionAnswers } from "services/index";
import { toast } from "Layout/slice";
import { parseErrorMessage } from "utils";
import { contains } from "utils/index";
import { Input } from "components/Form/Input";
import {
  QuestionContent,
  // QuestionDescription,
  QuestionContainer,
} from "./IntroQuestions.styles";
import { useNavigation } from "utils/navigate";
import { DropdownNewCustom } from "components/Dropdown/index";
import { AnnualIncomeSlider } from "components/AnnualIncomeSlider/index";
import {
  useLocalStorageSync,
  useCleanupOldKycData,
} from "hooks/useLocalStorage";
import { QuestionnaireStorageKeys } from "./constants";

const IntroQuestions = () => {
  const dispatch = useDispatch();
  const { isLoading, questions } = useQuestionnaire("recommendation");
  const {
    navigateToSignupSurmountRecommendationsKycQuestions,
    navigateToSignupSurmountAccountTaxW9Agreement,
  } = useNavigation();

  // State management
  const [selectedChoices, setSelectedChoices] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [otherInputs, setOtherInputs] = useState({});
  const [currentStep, setCurrentStep] = useState(1);

  // Clean up old localStorage data
  useCleanupOldKycData();

  // Sync state with localStorage
  useLocalStorageSync(
    QuestionnaireStorageKeys.SELECTED_CHOICES,
    selectedChoices,
    setSelectedChoices,
    {},
    true
  );
  useLocalStorageSync(
    QuestionnaireStorageKeys.OTHER_INPUTS,
    otherInputs,
    setOtherInputs,
    {},
    true
  );

  // Memoized computed values
  const currentStepQuestions = useMemo(() => {
    if (currentStep === 1) return questions.slice(0, 4);
    return questions.slice(4);
  }, [currentStep, questions]);

  // Helper functions with useCallback
  const isQuestionAnswered = useCallback(
    (question) => {
      const currentValue = selectedChoices[question.slug];
      if (question.type === "single_choice") {
        return currentValue && currentValue !== "";
      }
      return (
        currentValue && Array.isArray(currentValue) && currentValue.length > 0
      );
    },
    [selectedChoices]
  );

  const getCurrentQuestionIndex = useCallback(() => {
    for (let i = 0; i < currentStepQuestions.length; i++) {
      if (!isQuestionAnswered(currentStepQuestions[i])) {
        return i;
      }
    }
    return currentStepQuestions.length - 1;
  }, [currentStepQuestions, isQuestionAnswered]);

  const isQuestionInteractive = useCallback(
    (index) => {
      const currentQuestionIndex = getCurrentQuestionIndex();
      return index <= currentQuestionIndex;
    },
    [getCurrentQuestionIndex]
  );

  const areCurrentStepQuestionsAnswered = useMemo(() => {
    return currentStepQuestions.every((question) =>
      isQuestionAnswered(question)
    );
  }, [currentStepQuestions, isQuestionAnswered]);

  // Event handlers with useCallback
  const handleRadioChange = useCallback((questionSlug, value) => {
    setSelectedChoices((prev) => ({ ...prev, [questionSlug]: value }));
  }, []);

  const handleDropdownChange = useCallback((questionSlug, value) => {
    setSelectedChoices((prev) => ({ ...prev, [questionSlug]: value }));
  }, []);

  const handleCheckboxChange = useCallback(
    (questionSlug, values) => {
      setSelectedChoices((prev) => ({ ...prev, [questionSlug]: values }));

      const currentQuestion = questions.find((q) => q.slug === questionSlug);
      const otherChoice = currentQuestion?.choices.find((c) =>
        contains(c.text.toLowerCase(), "other")
      );
      const otherChoiceSlug = otherChoice?.slug;

      if (otherChoiceSlug && !values.includes(otherChoiceSlug)) {
        setOtherInputs((prev) => ({ ...prev, [questionSlug]: "" }));
      }
    },
    [questions]
  );

  const handleOtherTextChange = useCallback(
    (questionSlug, e) => {
      const textValue = e.target.value;
      setOtherInputs((prev) => ({ ...prev, [questionSlug]: textValue }));

      const currentQuestion = questions.find((q) => q.slug === questionSlug);
      const otherChoice = currentQuestion?.choices.find((c) =>
        contains(c.text.toLowerCase(), "other")
      );
      const otherChoiceSlug = otherChoice?.slug;
      const currentValue = selectedChoices[questionSlug] || [];

      if (
        textValue.trim() &&
        otherChoiceSlug &&
        !currentValue.includes(otherChoiceSlug)
      ) {
        setSelectedChoices((prev) => ({
          ...prev,
          [questionSlug]: [...currentValue, otherChoiceSlug],
        }));
      } else if (
        !textValue.trim() &&
        otherChoiceSlug &&
        currentValue.includes(otherChoiceSlug)
      ) {
        setSelectedChoices((prev) => ({
          ...prev,
          [questionSlug]: currentValue.filter((id) => id !== otherChoiceSlug),
        }));
      }
    },
    [questions, selectedChoices]
  );

  const saveAnswers = useCallback(async () => {
    setIsSaving(true);
    try {
      const formattedAnswers = Object.entries(selectedChoices).map(
        ([questionSlug, choiceValue]) => {
          if (Array.isArray(choiceValue)) {
            const answer = {
              question: questionSlug,
              choices: choiceValue,
            };
            const otherText = otherInputs[questionSlug];
            if (otherText) answer.text = otherText;
            return answer;
          }
          return {
            question: questionSlug,
            choices: [choiceValue],
          };
        }
      );

      await saveQuestionAnswers({ answers: formattedAnswers });
    } catch (error) {
      console.error("Failed to save answers:", error);
      dispatch(
        toast({
          type: "error",
          body: parseErrorMessage(error),
        })
      );
    } finally {
      navigateToSignupSurmountAccountTaxW9Agreement();
      setIsSaving(false);
    }
  }, [
    selectedChoices,
    otherInputs,
    dispatch,
    navigateToSignupSurmountAccountTaxW9Agreement,
  ]);

  const handleBack = useCallback(() => {
    if (currentStep === 2) {
      setCurrentStep(1);
    } else {
      navigateToSignupSurmountRecommendationsKycQuestions();
    }
  }, [currentStep, navigateToSignupSurmountRecommendationsKycQuestions]);

  const handleContinue = useCallback(() => {
    if (currentStep === 1) {
      if (areCurrentStepQuestionsAnswered) {
        setCurrentStep(2);
      }
    } else {
      saveAnswers();
    }
  }, [currentStep, areCurrentStepQuestionsAnswered, saveAnswers]);

  // Render functions
  const renderQuestionComponent = useCallback(
    (question, index) => {
      const choices =
        question.choices?.map((choice) => ({
          label: choice.text,
          value: choice.slug,
          slug: choice.slug,
        })) || [];

      const otherChoice = question.choices?.find((c) =>
        contains(c.text.toLowerCase(), "other")
      );
      const isSingleChoice = question.type === "single_choice";
      const currentValue = isSingleChoice
        ? selectedChoices[question.slug] || ""
        : selectedChoices[question.slug] || [];
      const isInteractive = isQuestionInteractive(index);

      if (
        question.slug === "current_employment_status" ||
        question.slug === "investable_percentage"
      ) {
        return (
          <ChoiceWrapper>
            <DropdownNewCustom
              title={question.text}
              options={choices}
              value={currentValue}
              onChange={(value) => handleDropdownChange(question.slug, value)}
              name={`question-${question.slug}`}
              disabled={!isInteractive}
              placeholder="Select an option"
              openUpward={question.slug === "investable_percentage"}
            />
            {/* {question.description && (
              <QuestionDescription>{question.description}</QuestionDescription>
            )} */}
          </ChoiceWrapper>
        );
      }

      if (
        question.text &&
        question.text.toLowerCase().includes("annual income")
      ) {
        return (
          <ChoiceWrapper>
            <AnnualIncomeSlider
              title={question.text}
              value={currentValue}
              onChange={(value) => handleDropdownChange(question.slug, value)}
              name={`question-${question.slug}`}
              disabled={!isInteractive}
              options={question.choices || []}
            />
            {/* {question.description && (
              <QuestionDescription>{question.description}</QuestionDescription>
            )} */}
          </ChoiceWrapper>
        );
      }

      if (isSingleChoice) {
        return (
          <ChoiceWrapper>
            <RadioGroup
              title={question.text}
              options={choices}
              value={currentValue}
              onChange={(value) => handleRadioChange(question.slug, value)}
              name={`question-${question.slug}`}
              disabled={!isInteractive}
            />
            {/* {question.description && (
              <QuestionDescription>{question.description}</QuestionDescription>
            )} */}
          </ChoiceWrapper>
        );
      }

      return (
        <ChoiceWrapper>
          <CheckboxGroup
            title={question.text}
            options={choices}
            value={currentValue}
            onChange={(values) => handleCheckboxChange(question.slug, values)}
            name={`question-${question.slug}`}
            disabled={!isInteractive}
          />
          {/* {question.description && (
            <QuestionDescription>{question.description}</QuestionDescription>
          )} */}
          {otherChoice && currentValue.includes(otherChoice.slug) && (
            <Input
              placeholder="Please specify"
              value={otherInputs[question.slug] || ""}
              onChange={(event) => handleOtherTextChange(question.slug, event)}
              className="mt-3"
              disabled={!isInteractive}
            />
          )}
        </ChoiceWrapper>
      );
    },
    [
      selectedChoices,
      otherInputs,
      isQuestionInteractive,
      handleRadioChange,
      handleDropdownChange,
      handleCheckboxChange,
      handleOtherTextChange,
    ]
  );

  const renderQuestion = useCallback(
    (question, index) => {
      const isAnswered = isQuestionAnswered(question);
      const currentQuestionIndex = getCurrentQuestionIndex();
      const isCurrentOrPrevious = index <= currentQuestionIndex;
      const isInteractive = isQuestionInteractive(index);

      return (
        <QuestionContent
          key={question.slug}
          $isAnswered={isAnswered}
          $isCurrentOrPrevious={isCurrentOrPrevious}
          $isInteractive={isInteractive}
        >
          {renderQuestionComponent(question, index)}
        </QuestionContent>
      );
    },
    [
      isQuestionAnswered,
      getCurrentQuestionIndex,
      isQuestionInteractive,
      renderQuestionComponent,
    ]
  );

  // Loading and empty states
  if (isLoading) return <Loader />;
  if (!questions.length) return <>No questions found.</>;

  return (
    <SignupFlowWrapperContainer>
      <SignupFlowContent>
        <QuestionContainer>
          {currentStepQuestions.map((question, index) =>
            renderQuestion(question, index)
          )}
        </QuestionContainer>
      </SignupFlowContent>

      <SignupFlowFooter>
        <FooterRight>
          <Button variant="secondary" onClick={handleBack}>
            Go Back
          </Button>
          <Button
            variant="primary"
            onClick={handleContinue}
            disabled={isSaving || !areCurrentStepQuestionsAnswered}
          >
            {isSaving ? <Loader className="on-btn" /> : "Continue"}
          </Button>
        </FooterRight>
      </SignupFlowFooter>
    </SignupFlowWrapperContainer>
  );
};

export default IntroQuestions;
